// F3 primitive: read brain-MCP read-spans from Log Analytics.
// Mirrors ledger-traces-client.ts patterns: AppDependencies, DependencyType=="AI",
// todynamic(Properties), exact equality (NEVER `has` — term-tokenizes ids → false misses).
// Boundary-clean: no apps/* imports. MemoryReadEvent imported from @m8t-stack/api-contract.
import { LogsQueryClient, LogsQueryResultStatus } from "@azure/monitor-query";
import type { TokenCredential } from "@azure/core-auth";
import type { MemoryReadEvent } from "@m8t-stack/api-contract";

// Safe-ID guard for KQL interpolation — same pattern as ledger-traces-client.
const SAFE_ID = /^[A-Za-z0-9_]+$/;
// conv ids must match the canonical Foundry shape.
const CONV_ID = /^conv_[A-Za-z0-9]+$/;

// ── KQL builders ──────────────────────────────────────────────────────────────

/**
 * Build the KQL that fetches brain-MCP `get_file_contents` read-spans for a
 * single conversation. Guard convId against injection; return a take-0 no-op on
 * a bad id. Uses exact equality for all id comparisons — NEVER `has`.
 */
export function buildBrainReadsKql(convId: string): string {
  if (!CONV_ID.test(convId)) return "AppDependencies | take 0";
  return `AppDependencies
| where DependencyType=="AI"
| extend P=todynamic(Properties)
| where tostring(P["gen_ai.operation.name"])=="execute_tool"
| where tostring(P["gen_ai.tool.name"])=="mcp_brain.get_file_contents"
| extend conv=tostring(P["gen_ai.conversation.id"]), args=tostring(P["gen_ai.tool.call.arguments"]), span=tostring(P["gen_ai.tool.call.id"])
| where conv == '${convId}'
| project args, span`;
}

/** A parsed row from the brain-reads query result table. */
export interface BrainReadRow {
  args: string;
  span: string;
}

// ── Table→rows mapper ─────────────────────────────────────────────────────────

interface LogsTableLike {
  columnDescriptors: { name: string }[];
  rows: unknown[][];
}

function tableToRows(t: LogsTableLike): BrainReadRow[] {
  const idx = Object.fromEntries(t.columnDescriptors.map((c, i) => [c.name, i]));
  const s = (r: unknown[], k: string): string => {
    const v = r[idx[k]];
    if (v == null) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    return "";
  };
  return t.rows.map((r) => ({ args: s(r, "args"), span: s(r, "span") }));
}

// ── Parse ─────────────────────────────────────────────────────────────────────

/**
 * Map raw BrainReadRows to MemoryReadEvents. JSON.parse each args; read .path;
 * emit { path, tool: "get_file_contents", spanRef }. Skip rows that don't parse
 * or lack a string path.
 */
export function parseBrainReads(rows: BrainReadRow[]): MemoryReadEvent[] {
  const out: MemoryReadEvent[] = [];
  for (const row of rows) {
    try {
      const parsed = JSON.parse(row.args) as unknown;
      if (typeof parsed !== "object" || parsed === null) continue;
      const path = (parsed as Record<string, unknown>).path;
      if (typeof path !== "string") continue;
      out.push({ path, tool: "get_file_contents", spanRef: row.span || undefined });
    } catch {
      // malformed JSON — skip
    }
  }
  return out;
}

// ── Retry/backoff helpers ─────────────────────────────────────────────────────

interface RetryOptions {
  maxAttempts?: number;
  /** Injectable delay function (default: real setTimeout). Pass a no-op in tests. */
  delay?: (ms: number) => Promise<void>;
}

const realDelay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

async function withRetry<T>(
  fn: () => Promise<T>,
  opts: RetryOptions = {},
): Promise<T> {
  const max = opts.maxAttempts ?? 3;
  const delayFn = opts.delay ?? realDelay;
  let lastError: unknown;
  for (let attempt = 0; attempt < max; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (attempt < max - 1) {
        await delayFn(500 * Math.pow(2, attempt));
      }
    }
  }
  throw lastError;
}

// ── Querier seam type ─────────────────────────────────────────────────────────

/** The subset of LogsQueryClient.queryWorkspace we depend on — injectable for tests. */
export type LogsQuerier = (
  workspaceId: string,
  query: string,
  timespan: { startTime: Date; endTime: Date },
) => Promise<{ status: LogsQueryResultStatus; tables: LogsTableLike[] }>;

/** Tri-state result for fetchBrainReads*.
 *  ok:true  — the LA query ran and returned an authoritative result (events may be []).
 *  ok:false — the query failed (throttle, network error, bad convId, empty tables on
 *             a Success status that carries no data). Callers MUST treat ok:false as
 *             "degraded", never as "never_read". */
export interface BrainReadsResult {
  ok: boolean;
  events: MemoryReadEvent[];
}

// ── fetchBrainReads (querier-seam variant for tests) ─────────────────────────

/**
 * Fetch brain-MCP read spans for a conversation using an injected querier.
 * Retries up to maxAttempts (default 3) with exponential backoff.
 *
 * M1 fix — returns {ok, events} so callers can distinguish:
 *   ok:true,  events:[]     → query ran, nothing read → "never_read"
 *   ok:false, events:[]     → query failed/throttled → "degraded"
 *
 * ok:false when:
 *   – bad convId (injection guard short-circuits before any query)
 *   – non-Success status after all retries
 *   – empty tables array on a Success response (no data was returned)
 *   – all retry attempts threw
 */
export async function fetchBrainReadsWithQuerier(
  querier: LogsQuerier,
  workspaceId: string,
  convId: string,
  window: { from: Date; to: Date },
  opts: RetryOptions = {},
): Promise<BrainReadsResult> {
  const kql = buildBrainReadsKql(convId);
  if (kql === "AppDependencies | take 0") return { ok: false, events: [] };
  try {
    const result = await withRetry(
      () => querier(workspaceId, kql, { startTime: window.from, endTime: window.to }),
      opts,
    );
    if (result.status !== LogsQueryResultStatus.Success) return { ok: false, events: [] };
    if (result.tables.length === 0) return { ok: false, events: [] };
    return { ok: true, events: parseBrainReads(tableToRows(result.tables[0])) };
  } catch {
    return { ok: false, events: [] };
  }
}

/**
 * Public API: fetch brain-MCP read spans using a real Azure credential.
 * Accepts optional RetryOptions for testing (delay injection).
 * Returns {ok, events} — see BrainReadsResult for the tri-state contract.
 */
export async function fetchBrainReads(
  credential: TokenCredential,
  workspaceId: string,
  convId: string,
  window: { from: Date; to: Date },
  opts: RetryOptions = {},
): Promise<BrainReadsResult> {
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- LogsQueryClient deprecated in favour of @azure/monitor-query-logs; migration deferred (separate dependency-update PR)
  const client = new LogsQueryClient(credential);
  const querier: LogsQuerier = (ws, kql, ts) =>
    client.queryWorkspace(ws, kql, { startTime: ts.startTime, endTime: ts.endTime }) as Promise<{ status: LogsQueryResultStatus; tables: LogsTableLike[] }>;
  return fetchBrainReadsWithQuerier(querier, workspaceId, convId, window, opts);
}

// ── convIdFromResponseId (querier-seam variant for tests) ─────────────────────

/**
 * Resolve a Foundry responseId → conversationId using Log Analytics.
 * Mirrors makeConvIdFn in apps/cli/src/commands/dream/run.ts (reimplemented here
 * to keep the agent-ledger/read package free of apps/* imports).
 * Guards respId with SAFE_ID; validates the result against CONV_ID.
 * Returns null on miss, bad shape, or any error.
 */
export async function convIdFromResponseIdWithQuerier(
  querier: LogsQuerier,
  workspaceId: string,
  respId: string,
  window: { from: Date; to: Date },
): Promise<string | null> {
  if (!SAFE_ID.test(respId)) return null;
  const kql = `AppDependencies
| where DependencyType == "AI"
| extend P = todynamic(Properties)
| extend resp = tostring(P["gen_ai.response.id"]), conv = tostring(P["gen_ai.conversation.id"])
| where resp == '${respId}'
| where isnotempty(conv)
| project conv
| take 1`;
  try {
    const result = await querier(workspaceId, kql, { startTime: window.from, endTime: window.to });
    if (result.status !== LogsQueryResultStatus.Success || result.tables.length === 0) return null;
    const table = result.tables[0];
    if (table.rows.length === 0) return null;
    const row = table.rows[0];
    const idx = table.columnDescriptors.findIndex((c) => c.name === "conv");
    const rawConv = idx >= 0 ? row[idx] : undefined;
    const conv = typeof rawConv === "string" ? rawConv : "";
    return CONV_ID.test(conv) ? conv : null;
  } catch {
    return null;
  }
}

/**
 * Public API: resolve a responseId → conversationId using a real Azure credential.
 */
export async function convIdFromResponseId(
  credential: TokenCredential,
  workspaceId: string,
  respId: string,
  window: { from: Date; to: Date },
): Promise<string | null> {
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- LogsQueryClient deprecated; migration deferred
  const client = new LogsQueryClient(credential);
  const querier: LogsQuerier = (ws, kql, ts) =>
    client.queryWorkspace(ws, kql, { startTime: ts.startTime, endTime: ts.endTime }) as Promise<{ status: LogsQueryResultStatus; tables: LogsTableLike[] }>;
  return convIdFromResponseIdWithQuerier(querier, workspaceId, respId, window);
}
